<script lang="ts">
  import type { ReaderSidebarItem } from '../../types/reader.interface';
  import InlineSvg from '../elements/InlineSvg.svelte';
  import Tooltip from '../elements/Tooltip.svelte';
  import { fly } from 'svelte/transition';
  import { favoriteLibraryNames } from '../../store';
  import Chevron from '../elements/Chevron.svelte';
  import AnimationButton from '../elements/AnimationButton.svelte';
  import { createEventDispatcher } from 'svelte';

  export let collapse = false;
  export let rsScroll: number = 0;
  export let item: ReaderSidebarItem;
  export let active = false;
  export let featuredItem = false;
  export let itemSaved = false;
  export let className: 'categories' | 'sources';
  export let isMobile = false;
  export let itemCount = 0;

  let heart: HTMLSpanElement;
  let dispatch = createEventDispatcher();

  $: animateHeart(itemSaved);
  $: isFavorite = $favoriteLibraryNames.includes(item.name);
  $: icon = isFavorite ? '/img/icon/library/heart-fill.svg' : '';

  function animateHeart(state) {
    if (state) {
      heart?.classList.add('liked');
    } else {
      heart?.classList.remove('liked');
    }
  }

  function onFavoriteClicked() {
    if (isFavorite) {
      favoriteLibraryNames.remove(item.name);
    } else {
      dispatch('saved');
      favoriteLibraryNames.add(item.name);
    }
  }
</script>

<li
  class="rsli"
  class:active={!isMobile && active}
  class:ddr={isMobile && item.name === 'Daily Report'}
  role="button"
  on:click
>
  <Tooltip position="right" description={!!item.description} hide={!collapse} {rsScroll}>
    {#if item.icon}
      {#if item.name === 'Saved Items'}
        <span
          class="rsli-icon"
          bind:this={heart}
          transition:fly={{ x: -20, duration: 200 }}
        >
          <div class="heart-animation-1" />
          <div class="heart-animation-2" />
          <InlineSvg src={item.icon} />
        </span>
      {:else}
        <span
          class="rsli-icon"
          transition:fly={{ x: -20, duration: 200 }}
          class:badge={item.name === 'Newsletter Inbox'}
        >
          <InlineSvg src={item.icon} />
        </span>
      {/if}
    {:else if item.image}
      <span class="rsli-image" transition:fly={{ x: -20, duration: 200 }}>
        <img src={item.image} alt={item.name} />
      </span>
    {/if}
    <div slot="tip">
      {#if item.description}
        <span class="tip-title">{item.name}</span><br />
        <span class="tip-description">
          {item.description}
        </span>
      {:else}
        {item.name}
      {/if}
    </div>
  </Tooltip>
  {#if !collapse}
    {#if className === 'categories'}
      <h2
        class="rsli-title"
        title={item.description ? `${item.name} \n${item.description}` : ''}
        transition:fly={{ x: -20, duration: 200 }}
      >
        {item.name}
      </h2>
    {:else if !isMobile}
      <h3
        class="rsli-title"
        title={item.description ? `${item.name} \n${item.description}` : ''}
        transition:fly={{ x: -20, duration: 200 }}
      >
        {item.name}
      </h3>
      {#if !featuredItem}
        <div class="sprite" class:active={isFavorite}>
          <AnimationButton
            class="bookmark-animated"
            defaultIcon="/img/icon/library/heart.svg"
            bind:icon
            sprite="/img/sprite.png"
            active={isFavorite}
            steps={29}
            duration="0.3s"
            size={42}
            frameSize={42}
            on:click={onFavoriteClicked}
          />
        </div>
      {/if}
    {:else}
      <a href={item.url} target="_blank" rel="noopener noreferrer" class="rsli-item">
        <h3
          class="rsli-title"
          title={item.description ? `${item.name} \n${item.description}` : ''}
          transition:fly={{ x: -20, duration: 200 }}
        >
          {item.name}
        </h3>
        <p class="description">
          {item.description}
        </p>
      </a>
      <div class="action-buttons">
        <div class="sprite" class:active={isFavorite}>
          <AnimationButton
            class="bookmark-animated"
            defaultIcon="/img/icon/library/heart.svg"
            bind:icon
            sprite="/img/sprite.png"
            active={isFavorite}
            steps={29}
            duration="0.3s"
            size={42}
            frameSize={42}
            on:click={onFavoriteClicked}
          />
        </div>
        <a class="new-tab" href={item.url} target="_blank" rel="noopener noreferrer">
          <InlineSvg src="/img/icon/new-tab.svg" width="16px" height="16px" />
        </a>
      </div>
    {/if}
    {#if item.name === 'Saved Items' || (isMobile && itemCount)}
      <span class="si-prepend">
        <span>
          {item.name === 'Saved Items' ? $favoriteLibraryNames.length : itemCount}
        </span>
      </span>
      {#if isMobile}
        <span class="arrow">
          <Chevron direction="right" />
        </span>
      {/if}
    {/if}
  {/if}
  {#if featuredItem && !isMobile}
    {#if !collapse}
      <Tooltip {rsScroll}>
        <InlineSvg src="/img/icon/library/star.svg" width="12px" />
        <span slot="tip">Featured Source</span>
      </Tooltip>
    {:else}
      <InlineSvg
        src="/img/icon/library/star.svg"
        width="10px"
        push={{ position: 'absolute', top: '4px', right: '10px' }}
      />
    {/if}
  {/if}
</li>

<style lang="scss">
  @import '../../styles/mixins';

  .rsli {
    --svg-stroke: var(--color-blue-gray--200);
    --svg-fill: var(--color-blue-gray--200);
    position: relative;
    height: 40px;
    padding-left: 24px;
    padding-right: 12px;
    display: flex;
    align-items: center;
    color: var(--color-blue-gray--300);
    cursor: pointer;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 24px;
    }

    &:hover {
      color: var(--color-blue-gray--900);
      --svg-stroke: var(--color-blue-gray--900);
      --svg-fill: var(--color-blue-gray--900);

      .badge::after {
        background-color: #e2264ddd;
      }
    }

    &.active {
      color: var(--color-blue-gray--900);
      --svg-stroke: var(--color-blue-gray--900);
      --svg-fill: var(--color-blue-gray--900);

      &:before {
        content: '';
        position: absolute;
        left: 0;
        width: 4px;
        height: 24px;
        top: 50%;
        transform: translateY(-50%);
        background-color: var(--color-blue-gray--900);
        border-radius: 0 2px 2px 0;
      }

      .rsli-title {
        @include font(Semibold);
      }

      .si-prepend {
        color: var(--color-gray--0);
        background-color: var(--color-blue-gray--600);
      }
    }

    .si-prepend {
      height: 20px;
      @include font(Semibold);
      color: var(--color-blue-gray--200);
      font-size: 11px;
      background-color: var(--color-gray--0);
      padding: 2px 7px;
      border-radius: 5px;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
      margin-right: 5px;
      user-select: none;

      &:empty {
        display: none;
      }
    }

    .arrow {
      margin-left: 12px;
    }

    :global(.tooltip-container){
      margin-left: 0;
    }
  }

  .rsli-icon {
    display: inline-block;
    text-align: center;
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    margin-right: 12px;
    user-select: none;
    --svg-width: 16px;
    position: relative;
    animation: heartUnlike 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;

    &:empty {
      background-color: var(--color-blue-gray--100);
      border-radius: 100%;
    }

    &.badge {
      position: relative;

      &::after {
        content: '1';
        position: absolute;
        top: -5px;
        left: -9px;
        width: 16px;
        height: 16px;
        border-radius: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
        @include font-narrow(Bold);
        font-size: 11px;
        line-height: 16px;
        color: white;
        background: #e2264d;
        box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.34);
      }
    }

    :global(svg [stroke]:not([stroke='none'])) {
      stroke: var(--svg-stroke) !important;
    }

    :global(svg [fill]:not([fill='none'])) {
      fill: var(--svg-fill) !important;
    }
  }

  .rsli-icon.liked {
    :global(svg [stroke]:not([stroke='none'])) {
      stroke: transparent !important;
    }

    :global(svg [fill]:not([fill='none'])) {
      fill: transparent !important;
    }
  }

  .rsli-image {
    display: inline-block;
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    margin-right: 12px;
    text-align: center;
    user-select: none;

    img {
      width: 100%;
    }
  }
  .rsli-title {
    flex: 1;
    @include font(Medium);
    max-width: 140px;
    font-size: 14px;
    line-height: 20px;
    transition: all 0.1s ease-in;
    user-select: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:empty {
      height: 10px;
      background-color: var(--color-blue-gray--100);
      border-radius: 100px;
    }

    & + :global(.tooltip-container) {
      flex: 0;
      justify-content: flex-end !important;
      margin-left: 0
    }
  }

  .tip-title {
    @include font();
    font-size: 14px;
    line-height: 21px;
    font-weight: 600;
    color: var(--color-blue-gray--50);
  }

  .tip-description {
    display: inline-block;
    margin-top: 3px;
    @include font();
    font-size: 11px;
    line-height: 14px;
    font-weight: 500;
    color: var(--color-blue-gray--150);
  }

  .sprite {
    display: flex;
    :global(.animation-button) {
      border-radius: 5px;
      background-color: transparent;
      transition: all 0.5s ease-in;

      &:not(.active) {
        :global(svg path) {
          transition: all 0.5s ease-in;
          fill: var(--color-blue-gray--700) !important;
        }
      }
    }

    :global(.animation-button.active) {
      opacity: 1;
      visibility: visible;
    }

    :global(.bookmark-animated.active) {
      --svg-fill: var(--color-red--500);
    }
  }

  :global(.sources) {
    @media screen and (min-width: 601px) {
      .rsli {
        height: 38px;
        margin-bottom: 6px;
        padding-left: 16px;
        padding-right: 16px;

        .rsli-title {
          font-size: 13px;
        }
      }
    }
  }

  :global(.tooltip) {
    max-width: 236px;
    white-space: initial !important;
  }

  :global(.dark-mode) {
    .rsli {
      color: var(--color-blue-gray--300);

      :not(.tooltip-container) {
        :global(svg [stroke]:not([stroke='none'])) {
          transition: all 0.1s ease-in;
          stroke: var(--color-blue-gray--300) !important;
        }

        :global(svg [fill]:not([fill='none'])) {
          transition: all 0.1s ease-in;
          fill: var(--color-blue-gray--300) !important;
        }
      }

      &:not(.active):hover {
        color: var(--color-blue-gray--50);
        :not(.tooltip-container) {
          :global(svg [stroke]:not([stroke='none'])) {
            stroke: var(--color-blue-gray--50) !important;
          }

          :global(svg [fill]:not([fill='none'])) {
            fill: var(--color-blue-gray--50) !important;
          }
        }
      }

      &.active {
        color: var(--color-blue-gray--50);
        --svg-stroke: var(--color-blue-gray--50);
        --svg-fill: var(--color-blue-gray--50);

        &:before {
          background-color: var(--color-blue-gray--50);
        }

        .si-prepend {
          color: var(--color-blue-gray--600);
          background-color: var(--color-text-library-active);
        }
      }

      .rsli-icon:global(.liked) {
        :global(svg [stroke]:not([stroke='none'])) {
          stroke: transparent !important;
        }

        :global(svg [fill]:not([fill='none'])) {
          fill: transparent !important;
        }
      }
      .sprite {
        &:not(.active) {
          :global(svg path) {
            transition: all 0.5s ease-in;
            fill: var(--color-blue-gray--50) !important;
          }
        }
      }
    }

    .rsli-icon {
      &:empty {
        background-color: var(--color-blue-gray--400);
      }
    }

    .rsli-title {
      &:empty {
        background-color: var(--color-blue-gray--400);
      }
    }

    .si-prepend {
      color: var(--color-gray--0);
      background-color: var(--color-blue-gray--600);
    }
  }

  // :global(.collapse .tooltip-container + .tooltip-container) {
  //   margin-left: -56px;
  // }

  // :global(.collapse .tooltip-container + .tooltip-container .svg-wrapper) {
  //   transform: scale(0.7);
  // }

  // :global(.collapse .tooltip-container + .tooltip-container .tooltip.top) {
  //   margin-left: -66px;
  // }

  :global(.dark-mode .categories) {
    .rsli {
      &.active {
        color: var(--color-text-library-active);
        :global(svg [stroke]:not([stroke='none'])) {
          stroke: var(--color-fill-library-active) !important;
        }

        :global(svg [fill]:not([fill='none'])) {
          fill: var(--color-fill-library-active) !important;
        }
        &:before {
          background-color: var(--color-fill-library-active);
        }
      }
    }
  }

  :global(.collapse .rsli) {
    padding: 0 !important;
  }

  :global(.collapse .rsli .tooltip-container) {
    padding: 24px;
  }

  :global(.rsli .tooltip-container) {
    z-index: initial !important;
  }

  :global(.rs-list .rsli .tooltip-container div.tooltip.right) {
    transform: translate(20px, calc(0px - var(--scrollY)));
  }

  :global(.rs-list .rsli .tooltip-container:hover div.tooltip.right) {
    transform: translate(28px, calc(0px - var(--scrollY)));
  }

  :global(.tooltip) {
    max-width: 236px;
    white-space: initial !important;
    background: var(--color-blue-gray--700) !important;
    box-shadow: 0px 4px 12px rgba(29, 29, 32, 0.08), 0px 1px 2px rgba(29, 29, 32, 0.04) !important ;
    color: var(--color-blue-gray--50) !important;
  }

  :global(.liked.rsli-icon) {
    animation: heartPulse 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;

    .heart-animation-1,
    .heart-animation-2 {
      background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjEiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAyMSAxOCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTAuMTAxIDQuNDE3UzguODk1LjIwNyA1LjExMS4yMDdjLTQuNDY1IDAtMTAuOTY3IDYuODQ2IDUuMDgyIDE3LjU5MkMyNS4yMzcgNy4wMyAxOS42NjUuMjAyIDE1LjUwMS4yMDJjLTQuMTYyIDAtNS40IDQuMjE1LTUuNCA0LjIxNXoiIGZpbGw9IiNGRjZFNkYiIGZpbGwtcnVsZT0iZXZlbm9kZCIvPjwvc3ZnPg==')
        no-repeat center;
      background-size: 100%;
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 16px;
      height: 14px;
      opacity: 0;
      z-index: 11;

      &::before,
      &::after {
        content: '';
        background: inherit;
        background-size: 100%;
        width: inherit;
        height: inherit;
        display: inherit;
        position: relative;
        top: inherit;
        left: inherit;
        opacity: 0;
      }
    }

    .heart-animation-1 {
      animation: heartFloatMain-1 1s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;

      &::before,
      &::after {
        width: 12px;
        height: 10px;
        visibility: hidden;
      }
      &::before {
        opacity: 0.6;
        animation: heartFloatSub-1 1s 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
      }
      &::after {
        animation: heartFloatSub-2 1s 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
        opacity: 0.75;
      }
    }

    .heart-animation-2 {
      animation: heartFloatMain-2 1s 0.1s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;

      &::before,
      &::after {
        width: 10px;
        height: 8px;
        visibility: hidden;
      }
      &::before {
        animation: heartFloatSub-3 1s 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
        opacity: 0.25;
      }
      &::after {
        animation: heartFloatSub-4 1s 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
        opacity: 0.4;
      }
    }

    .heart-animation-2 + :global(.svg-wrapper svg path) {
      animation: col 1s linear infinite;
      z-index: 2;
    }
  }

  // Animations
  @keyframes heartPulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.5);
    }
  }

  @keyframes heartUnlike {
    50% {
      transform: scale(0.75);
    }
  }

  @keyframes heartFloatMain-1 {
    0% {
      opacity: 0;
      transform: translate(0) rotate(0);
    }
    50% {
      opacity: 1;
      transform: translate(0, -25px) rotate(-20deg);
    }
  }

  @keyframes heartFloatMain-2 {
    0% {
      opacity: 0;
      transform: translate(0) rotate(0) scale(0);
    }
    50% {
      opacity: 0.9;
      transform: translate(-10px, -38px) rotate(25deg) scale(1);
    }
  }

  @keyframes heartFloatSub-1 {
    0% {
      visibility: hidden;
      transform: translate(0) rotate(0);
    }
    50% {
      visibility: visible;
      transform: translate(13px, -13px) rotate(30deg);
    }
  }

  @keyframes heartFloatSub-2 {
    0% {
      visibility: hidden;
      transform: translate(0) rotate(0);
    }
    50% {
      visibility: visible;
      transform: translate(18px, -10px) rotate(55deg);
    }
  }

  @keyframes heartFloatSub-3 {
    0% {
      visibility: hidden;
      transform: translate(0) rotate(0);
    }
    50% {
      visibility: visible;
      transform: translate(-10px, -10px) rotate(-40deg);
    }
    100% {
      transform: translate(-50px, 0);
    }
  }

  @keyframes heartFloatSub-4 {
    0% {
      visibility: hidden;
      transform: translate(0) rotate(0);
    }
    50% {
      visibility: visible;
      transform: translate(2px, -18px) rotate(-25deg);
    }
  }

  @keyframes col {
    0% {
      fill: transparent;
    }

    20% {
      fill: #ff6e6f;
    }

    70% {
      fill: #ff6e6f;
    }

    100% {
      fill: transparent;
    }
  }

  @media screen and (max-width: 600px) {
    .rsli {
      display: flex;
      border-bottom: 1px solid var(--color-blue-gray--100);
      height: initial;
      padding: 20px 24px;
      margin: 0;

      h2 {
        display: block;
        line-height: 24px;
      }

      .si-prepend {
        font-size: 11px;
        line-height: 16px;
        padding: 2px 8px;
        width: 28px;
        border-radius: 25px;
        text-align: center;
        user-select: none;

        span {
          display: block;
          margin-top: -1px !important;
          line-height: 17px;
        }
      }

      .rsli-item {
        display: flex;
        align-items: flex-start;
        flex-direction: column;

        .rsli-title {
          @include font(Medium);
          font-size: 15px;
          line-height: 24px;
          color: var(--color-blue-gray--600);
        }

        .description {
          display: flex;
          @include font(Medium);
          font-size: 12px;
          line-height: 130%;
          min-height: 16px;
          color: var(--color-blue-gray--400);
          padding-right: 24px;
          margin-top: 2px;
        }
      }

      .action-buttons {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        width: 76px;
        min-width: 76px;
        margin-left: auto;

        .new-tab {
          margin-top: 3px;
          --svg-stroke: var(--color-blue-gray--200);
        }
      }

      .rsli-image {
        margin-top: 5px;
      }

      &.ddr {
        display: none !important;
      }
    }

    :global(.sources .tooltip-container) {
      align-self: baseline !important;
      margin-left: initial !important;
    }

    :global(.dark-mode) {
      .rsli {
        border-bottom: 1px solid var(--color-blue-gray--800);

        &:not(:hover) .rsli-title {
          color: var(--color-blue-gray--200);
        }

        &:hover {
          .new-tab {
            --svg-stroke: var(--color-blue-gray--100);
          }
        }

        .rsli-title {
          color: var(--color-blue-gray--100);
        }

        .description {
          color: var(--color-blue-gray--400);
        }
      }
    }
  }

  @media screen and (min-width: 601px) {
    .sprite {
      position: absolute !important;
      right: 0px !important;
      background-image: linear-gradient(270deg, white 71.25%, rgba(21, 25, 34, 0) 100%);
      opacity: 1;
      transition: all 0.1s ease-in;
      &:not(.active) {
        opacity: 0;
      }
    }

    .rsli:hover {
      .sprite {
        opacity: 1;
      }
    }

    :global(.dark-mode) {
      .sprite {
        background-image: linear-gradient(
          270deg,
          #151922 71.25%,
          rgba(21, 25, 34, 0) 100%
        );
      }
    }
  }
</style>
